import {
  forwardRef,
  useId,
  type ChangeEvent,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '@/utils/cn';

interface SharedProps {
  /**
   * Label rendered above the field (Karla SemiBold 12/14, steel). Acts as
   * the field's accessible name when `aria-label` isn't supplied.
   */
  label: string;
  /** Inline placeholder rendered inside the field while empty. */
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  /**
   * When present, paints the field's border in the amber error color
   * (`#A50000`) and renders the same message on the right side of the
   * label header (Karla Regular 12/14).
   */
  error?: string;
  name?: string;
  id?: string;
  required?: boolean;
  /** Disable focus + interaction. */
  disabled?: boolean;
  /** Class merged onto the root field element (input or textarea). */
  className?: string;
  'aria-label'?: string;
}

interface SingleLineProps
  extends
    SharedProps,
    Pick<
      InputHTMLAttributes<HTMLInputElement>,
      'type' | 'autoComplete' | 'inputMode' | 'maxLength' | 'pattern'
    > {
  multiline?: false;
}

interface MultilineProps
  extends SharedProps, Pick<TextareaHTMLAttributes<HTMLTextAreaElement>, 'maxLength'> {
  multiline: true;
  rows?: number;
}

export type TextFieldProps = SingleLineProps | MultilineProps;

const FIELD_BASE = cn(
  'w-full rounded-[16px] border bg-transparent p-4',
  'text-[16px] font-normal leading-6 text-clearlab-midnight',
  'placeholder:text-clearlab-steel',
  'transition-colors duration-fast ease-in',
  'focus:outline-none',
  // The border IS the focus indicator — suppress the global focus-visible
  // blue ring so it doesn't render on top.
  'focus-visible:ring-0 focus-visible:ring-offset-0',
  'disabled:cursor-not-allowed disabled:opacity-60',
);

const BORDER_DEFAULT = cn(
  'border-clearlab-divider-darker',
  'hover:border-clearlab-midnight focus:border-clearlab-midnight',
);
// Error state — amber border (#A50000) for both resting and focused so the
// invalid signal stays even while the user clicks back in to fix it.
const BORDER_ERROR = 'border-[#A50000] focus:border-[#A50000]';

/**
 * Form text field — single-line `<input>` or multi-line `<textarea>` (when
 * `multiline` is set). Matches the figma contact form (node 147:1969):
 *
 *   - Label header above the field (Karla SemiBold 12/14, steel), with
 *     8px gap to the field.
 *   - Field: 1px `clearlab-divider-darker` border, `rounded-[16px]`,
 *     `p-4` (16px all sides), `text-[16px]/[24px]` value, steel placeholder.
 *   - Hover/focus border ramps to `clearlab-midnight`.
 *   - Error state (147:2020): border becomes `#A50000`, and the message is
 *     mirrored on the right side of the label header in the same amber
 *     (Karla Regular 12/14). The field's `aria-invalid` flips on, and the
 *     error text is wired as `aria-describedby` for screen readers.
 *
 * Behaviour matches a regular controlled field — the consumer owns the
 * `value` state and receives plain string updates via `onChange`.
 */
export const TextField = forwardRef<HTMLInputElement | HTMLTextAreaElement, TextFieldProps>(
  function TextField(props, ref) {
    const { label, placeholder, value, onChange, error, name, id, required, disabled, className } =
      props;
    const generatedId = useId();
    const fieldId = id ?? generatedId;
    const errorId = `${fieldId}-error`;
    const ariaLabel = props['aria-label'] ?? label;
    const fieldClass = cn(FIELD_BASE, error ? BORDER_ERROR : BORDER_DEFAULT, className);

    const header = (
      <div className="flex w-full items-start justify-between text-[12px] leading-[14px]">
        <label htmlFor={fieldId} className="font-semibold text-clearlab-steel">
          {label}
        </label>
        {error ? (
          <span id={errorId} role="alert" className="font-normal text-[#A50000]">
            {error}
          </span>
        ) : null}
      </div>
    );

    if (props.multiline) {
      const { rows = 8, maxLength } = props;
      return (
        <div className="flex w-full flex-col items-start gap-2">
          {header}
          <textarea
            ref={ref as React.Ref<HTMLTextAreaElement>}
            id={fieldId}
            name={name}
            value={value}
            rows={rows}
            maxLength={maxLength}
            required={required}
            disabled={disabled}
            placeholder={placeholder}
            aria-label={ariaLabel}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
            className={cn(fieldClass, 'resize-none')}
          />
        </div>
      );
    }

    const { type = 'text', autoComplete, inputMode, maxLength, pattern } = props;
    return (
      <div className="flex w-full flex-col items-start gap-2">
        {header}
        <input
          ref={ref as React.Ref<HTMLInputElement>}
          id={fieldId}
          name={name}
          type={type}
          value={value}
          autoComplete={autoComplete}
          inputMode={inputMode}
          maxLength={maxLength}
          pattern={pattern}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          className={fieldClass}
        />
      </div>
    );
  },
);
